import React, { useState, useEffect, useRef } from "react";
import { Hourglass, ArrowRight, Loader2, Bell, Compass, Share2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Crest } from "../components/Media";
import api from "../lib/api";

export default function Pending() {
  const { user, setUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const [notYet, setNotYet] = useState(false);
  const [declined, setDeclined] = useState(false);
  const timer = useRef(null);

  const checkStatus = async (manual) => {
    if (manual) setChecking(true);
    try {
      const { data } = await api.get("/auth/me");
      if (data.status === "approved" || data.role === "admin") {
        setUser(data); // triggers redirect to /feed via PendingRoute
        return;
      }
      if (data.status === "rejected") {
        setDeclined(true);
        if (timer.current) clearInterval(timer.current);
        return;
      }
      if (manual) setNotYet(true);
    } catch {}
    if (manual) setChecking(false);
  };

  // auto-advance the moment the Council admits them
  useEffect(() => {
    timer.current = setInterval(() => checkStatus(false), 15000);
    return () => { if (timer.current) clearInterval(timer.current); };
    /* eslint-disable-next-line */
  }, []);

  if (declined) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center flex flex-col items-center">
          <Crest className="h-16 w-16 mb-8" />
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white mb-4" data-testid="declined-heading">A note on your application</h1>
          <p className="font-body text-base text-zinc-400 max-w-md leading-relaxed">
            Thank you, {user?.name}, for applying and for sharing your work with us. After review, the Council isn't able to welcome you in at this time. This isn't a judgment of your worth, we grow slowly and intentionally to keep the space coherent. We've sent you an email with this note, and you're welcome to reapply down the road.
          </p>
          <a href="/c" target="_blank" rel="noopener noreferrer" data-testid="declined-compass-link" className="mt-8 inline-flex items-center gap-2 border border-white/15 text-zinc-200 hover:bg-white/5 font-body text-sm px-5 py-3 transition-colors">
            <Compass size={16} /> Explore the Compass
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center flex flex-col items-center">
          <Crest className="h-16 w-16 mb-8" />
          <Hourglass className="text-[#14A89A] mb-6" strokeWidth={1.5} size={28} />
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white mb-4">Your signal is received</h1>
          <p className="font-body text-base text-zinc-400 max-w-md leading-relaxed">
            Welcome, {user?.name}. Your application is with the Council now.
          </p>
          <p className="font-body text-sm text-[#14A89A] max-w-md leading-relaxed mt-3" data-testid="pending-next-note">
            Here's what happens next: a real person on the Council reads your words, and you'll be welcomed in with an email. Nothing more for you to do right now.
          </p>

          {/* Honest time + email expectations */}
          <div className="mt-6 max-w-md w-full space-y-3 text-left">
            <p className="font-body text-sm text-zinc-400 leading-relaxed" data-testid="pending-time-note">
              The Council reviews every application by hand, so this usually takes around <span className="text-white">24 hours</span>, we're not keeping you waiting on purpose.
            </p>
            <p className="font-body text-sm text-zinc-400 leading-relaxed" data-testid="pending-email-note">
              You'll get an <span className="text-white">email the moment a Council member reviews your application</span> (approved or declined), so there's no need to keep checking back. This page also opens on its own the instant you're approved.
            </p>
          </div>

          {/* Read & share the Compass */}
          <div className="mt-8 max-w-md w-full border border-[#14A89A]/25 bg-[#14A89A]/5 px-5 py-5 text-left" data-testid="pending-compass-cta">
            <div className="flex items-start gap-3">
              <Compass size={18} className="text-[#14A89A] shrink-0 mt-0.5" />
              <div>
                <p className="font-body text-sm text-zinc-200 leading-relaxed">
                  <span className="text-white">Please read the Compass before you enter.</span> It's our ethos and the instructions for how everything in Sovereign Signal works, genuinely worth the few minutes, because everything you'll need to know lives there.
                </p>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">
                  Have friends, family or followers you'd like to invite? Explore the Compass (our ethos and instructions for the app) and share it while you wait, feel free to share on social platforms too. Word of mouth is crucial for Sovereign Signal's growth.
                </p>
                <a href="/c" target="_blank" rel="noopener noreferrer" data-testid="pending-compass-link" className="mt-4 inline-flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-2.5 hover:bg-[#2BC4B5] transition-colors font-body text-sm">
                  <Share2 size={15} /> Read &amp; share the Compass
                </a>
              </div>
            </div>
          </div>

          {/* Notifications nudge */}
          <div className="mt-6 max-w-md w-full border border-white/10 px-5 py-4 flex items-start gap-3 text-left" data-testid="onboarding-notify-note">
            <Bell size={18} className="text-[#14A89A] shrink-0 mt-0.5" />
            <p className="font-body text-sm text-zinc-300 leading-relaxed">
              <span className="text-white">Add Sovereign Signal to your home screen and allow notifications</span> so you're signalled the moment you're approved - and whenever a sovereign friend reaches out. On iPhone, tap <span className="text-white">Share</span> in Safari &rarr; <span className="text-white">"Add to Home Screen."</span>
            </p>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-10 text-center flex flex-col items-center">
          <p className="font-body text-sm text-zinc-500 mb-4">Already admitted by the Council?</p>
          <button onClick={() => checkStatus(true)} disabled={checking} data-testid="enter-signal-button" className="inline-flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3.5 hover:bg-[#2BC4B5] transition-colors font-body disabled:opacity-40">
            {checking ? <Loader2 className="animate-spin" size={18} /> : <>Enter the Signal <ArrowRight size={18} /></>}
          </button>
          {notYet && (
            <p className="font-body text-xs text-zinc-500 mt-4" data-testid="not-yet-message">
              You haven't been admitted just yet. You'll be welcomed in the moment the Council approves you.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
